import { FC, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import useStore from '@hooks/useStore';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import DlocalBbvaBankTab from '../DlocalBbvaBankTab';
import GenericAnotherBankTab from '../GenericAnotherBankTab';

import useStyles from './styles';
import Grid from '@material-ui/core/Grid/Grid';
import ExpandMore from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import { Partner } from '@constants/paymentLink';

const GenericSpeiInfo: FC = () => {
  const {
    dlocal,
    arcus,
    fintoc,
    monato,
    links: {
      coBrandingConfig: { primaryColor, secondaryColor },
      paymentConfig: {
        paymentMethods: { spei },
      },
    },
  } = useStore();
  const css = useStyles({ primaryColor, secondaryColor });
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const speiData = useMemo(() => {
    switch (spei.partner) {
      case Partner.STP:
        return { ...dlocal.speiReference, resetSpeiReference: dlocal.resetSpeiReference };
      case Partner.FINTOC:
        return { ...fintoc.speiReference, resetSpeiReference: fintoc.resetSpeiReference };
      case Partner.MONATO:
        return { ...monato.speiReference, resetSpeiReference: monato.resetSpeiReference };
      case Partner.ARCUS:
      default:
        return { ...arcus.speiReference, resetSpeiReference: arcus.resetSpeiReference };
    }
  }, [
    spei.partner,
    dlocal.speiReference,
    arcus.speiReference,
    fintoc.speiReference,
    monato.speiReference,
  ]);

  const formattedAmount = Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(
    parseInt(speiData.amount),
  );

  const anotherBankInfo = [
    {
      key: 'another-bank-amount',
      label: t('checkoutScreen.payment.methods.spei.anotherBankTab.amount'),
      value: `${formattedAmount}`,
    },
    {
      key: 'another-bank-beneficiary-name',
      label: t('checkoutScreen.payment.methods.spei.anotherBankTab.beneficiaryName'),
      value: speiData.beneficiaryName,
    },
    {
      key: 'another-bank-clabe.',
      label: t('checkoutScreen.payment.methods.spei.anotherBankTab.clabe'),
      value: speiData.clabe,
      formattedValue: speiData.clabe.replace(/(.{4})(.{4})(.{4})(.+)/g, '$1 $2 $3 $4'),
    },
    {
      key: 'another-bank-reference-number',
      label: t('checkoutScreen.payment.methods.spei.anotherBankTab.referenceNumber'),
      value: speiData.referenceNumber,
    },
    {
      key: 'another-bank-name',
      label: t('checkoutScreen.payment.methods.spei.anotherBankTab.bankName'),
      value: speiData.bankName,
    },
  ];

  const cieAgreementInfo = [
    {
      title: t('checkoutScreen.payment.methods.spei.dlocal.bbvaBankTab.steps.1'),
    },
    {
      title: t('checkoutScreen.payment.methods.spei.dlocal.bbvaBankTab.steps.2'),
      items: [
        {
          key: 'bbva-bank-cie-agreement-number',
          label: t('checkoutScreen.payment.methods.spei.dlocal.bbvaBankTab.cieAgreementNumber'),
          value: speiData.cieAgreementNumber,
          copyValue: speiData.cieAgreementNumber,
          copyLabel: t('checkoutScreen.payment.spei.copied'),
        },
      ],
    },
    {
      title: t('checkoutScreen.payment.methods.spei.dlocal.bbvaBankTab.steps.3'),
      items: [
        {
          key: 'bbva-bank-reference',
          label: t('checkoutScreen.payment.methods.spei.dlocal.bbvaBankTab.referenceNumber'),
          value: speiData.referenceNumber,
          copyValue: speiData.referenceNumber,
          copyLabel: t('checkoutScreen.payment.spei.copied'),
        },
        {
          key: 'bbva-bank-cie-amount',
          label: t('checkoutScreen.payment.methods.spei.dlocal.bbvaBankTab.amount'),
          value: formattedAmount,
          copyValue: speiData.amount,
          copyLabel: t('checkoutScreen.payment.spei.copied'),
        },
      ],
    },
  ];

  const onTabChange = (_: React.ChangeEvent<Record<string, unknown>>, newTab: number) =>
    setSelectedTab(newTab);

  const getCurrentTab = () =>
    selectedTab ? (
      <DlocalBbvaBankTab cieAgreementInfo={cieAgreementInfo} />
    ) : (
      <GenericAnotherBankTab
        title={t('checkoutScreen.payment.methods.spei.anotherBankTab.instruction')}
        referenceInfo={anotherBankInfo}
        copyLabel={t('checkoutScreen.payment.spei.copied')}
      />
    );

  return (
    <div className={css.root}>
      <Grid container className={css.backControl}>
        <ExpandMore
          className={clsx(css.icon, css.backIcon)}
          onClick={speiData.resetSpeiReference}
        />
        <h1 className={css.speiFormTitle}>{t('checkoutScreen.payment.methods.spei.backTitle')}</h1>
      </Grid>
      <Tabs
        variant="fullWidth"
        value={selectedTab}
        onChange={onTabChange}
        classes={{
          indicator: css.indicator,
        }}
      >
        <Tab
          data-testid="generic-spei-info-tab-1"
          className={css.tab}
          label={t('checkoutScreen.payment.methods.spei.anotherBankTab.title')}
        />
        {/*
        <Tab
          data-testid="dlocal-spei-info-tab-2"
          className={css.tab}
          label={t('checkoutScreen.payment.methods.spei.dlocal.bbvaBankTab.title')}
        /> */}
      </Tabs>
      <div className={css.content}>{getCurrentTab()}</div>
    </div>
  );
};

export default observer(GenericSpeiInfo);
